import { useEffect, useRef } from 'react';
import { ArrayField, useFormApi, useFormState } from 'informed';
import { useDispatch, useReduxState } from 'hooks';
import { Button } from 'components/common';
import { DisplayField, FormGroup } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';

import type { Attachments } from 'shared-types';

interface Props {
  disabled?: boolean;
  field?: string;
  getUploadHandler: (fn: () => Promise<Attachments>) => void;
  required?: boolean;
  tooltip?: string;
  useUnwrapped?: boolean;
}

const MultipleAttachments = ({
  disabled = false,
  field = 'attachments',
  getUploadHandler,
  required = false,
  tooltip,
  useUnwrapped = false
}: Props) => {
  const { dispatch } = useDispatch();

  const formApi = useFormApi<{ [f: typeof field]: Attachments }>();
  const { errors, values } = useFormState<{ [f: typeof field]: Attachments }>();

  const { messages, theme } = useReduxState();

  const uploaders = useRef({});

  const uploadFiles = async () => {
    try {
      // deal with uploaders
      const uploadersValues = Object.values(uploaders.current);

      const validUploaders = uploadersValues.filter(
        (uploader: any) => uploader.state.file
      );

      const uploadedFileUrls = await Promise.all(
        validUploaders.map((uploader: any) => uploader.upload())
      );
      if (uploadedFileUrls.some((url) => !url)) {
        throw new Error(messages.commonUploadFileFailed);
      }

      return [
        ...(values[field] || []),
        ...uploadedFileUrls.map(({ filename, url }) => ({
          filename,
          fileUrl: url
        }))
      ];
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        throw err; // user already notified by the uploader, throw to prevent form from submitting
      } else {
        await dispatch.handleErrorDispatch(err, err.message);
      }
    }
  };

  useEffect(() => {
    getUploadHandler(uploadFiles);
  }, [getUploadHandler]);

  const clearAttachmentError = () => {
    if (errors[field]) {
      formApi.setError(field, undefined);
    }
  };

  const handleRemove = (
    index: number,
    fieldItem: string,
    callback: () => void
  ) => {
    if (values[field]?.[index]?.fileUrl) {
      formApi.setValue(
        field,
        values[field].filter((_, i) => i !== index)
      );
    }
    delete uploaders.current[fieldItem]; // clear the ref so validation won't see it as pending

    callback();
  };

  const validateAttachments = (value: Attachments) => {
    const hasUploaded = value?.some((item) => item.fileUrl);
    const hasPending = Object.values(uploaders.current).some(
      (uploader: { state: { file: unknown } }) => uploader.state.file
    );

    if (hasUploaded || hasPending) return undefined;

    return messages.commonAttachmentsRequired;
  };

  const Wrapper = useUnwrapped ? 'div' : FormGroup;

  return (
    <Wrapper
      className="wrapper"
      error={errors[field]}
      label={messages.commonAttachments}
      labelIsOptional={!required}
      {...(tooltip ? { 'aria-label': tooltip, 'data-tooltip': tooltip } : {})}
    >
      <ArrayField
        field={field}
        {...(required ? { validate: validateAttachments } : {})}
      >
        {({ add }) => (
          <>
            <ArrayField.Items>
              {({ remove, field: fieldItem, index }) => {
                const existingUploadedFile = values[field]?.[index];

                return (
                  <div className="file">
                    <Button
                      category="light"
                      icon="trash-alt"
                      onClick={() => handleRemove(index, fieldItem, remove)}
                      size="small"
                      style={{ minWidth: 30 }}
                      tooltip={messages.commonRemove}
                    />

                    {existingUploadedFile ? (
                      <div className="existing-file">
                        {existingUploadedFile.filename}
                      </div>
                    ) : (
                      <FileUploader
                        getUploader={(upl: any) =>
                          (uploaders.current = {
                            ...uploaders.current,
                            [fieldItem]: upl
                          })
                        }
                        keyPrefix="uploads/files/"
                        onFileAdded={clearAttachmentError}
                      />
                    )}
                  </div>
                );
              }}
            </ArrayField.Items>

            <Button
              category="light"
              disabled={disabled}
              icon="plus"
              onClick={add}
              size="small"
              style={{ marginTop: 5 }}
              text={messages.commonAddAttachment}
            />
          </>
        )}
      </ArrayField>

      <DisplayField field={field} />

      <style jsx>{`
        .wrapper {
          min-width: 200px;
        }
        .file {
          display: flex;
          gap: 10px;
          margin-bottom: 5px;
        }
        .existing-file {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.textColorLight};
          max-width: 100%;
          overflow: hidden;
          padding: 5px 10px;
        }
      `}</style>
    </Wrapper>
  );
};

export default MultipleAttachments;
